/* ThemeContext — motiv CELÉ APLIKACE (ne barvy vizitky poradce!).
 *
 * ⚠️ DVĚ RŮZNÉ VĚCI, KTERÉ SE UŽ JEDNOU SPLETLY:
 *   1) MOTIV APLIKACE  = tenhle soubor. Přepíná <html data-theme="…"> a tím
 *      barevné tokeny celého CRM. Ukládá se do localStorage("crm-theme").
 *   2) BARVY VIZITKY   = profile.vizitka_theme (pozadí/akcent/text klientské
 *      vizitky na /v/:slug). Edituje se v pages/VizitkaSettingsPage.js a ukládá
 *      se na profil do backendu. S motivem aplikace NEMÁ NIC SPOLEČNÉHO.
 * Nikdy nepojmenovávej lokální setter vizitky `setTheme` — v SettingsPage.js
 * takový mrtvý shadow ležel a při čtení kódu vypadal jako příčina chyby.
 *
 * Chování:
 *  - drží volbu v localStorage("crm-theme")
 *  - nastavuje <html data-theme="…">
 *  - první mount: přečte uloženou volbu, jinak spadne na "sage"
 *  - dává { theme, setTheme, themes, toggleTheme }
 *
 * Tokeny motivů žijí v /src/index.css pod [data-theme="sage"] / [data-theme="indigo"]
 * A TAKÉ v ink-board větvích (`:root:not([data-theme="indigo"]) .ink-board` a
 * `:root[data-theme="indigo"] .ink-board`). Nový motiv se musí doplnit na OBĚ
 * místa, jinak se v .ink-board obrazovkách (skoro celé CRM) vůbec neprojeví.
 *
 * 17. 8. 2026 — druhá vrstva: levá lišta. Barvy lišty ležely natvrdo
 * v components/LeftSidebar.js, takže na motivu nezávisely. Teď jedou přes
 * rodinu tokenů --sidebar* (index.css) a náhled níže z nich vychází, aby
 * dlaždice v Nastavení ukazovala PRAVDU, ne návrh, který se nikdy nepostavil.
 */
import React, { createContext, useContext, useEffect, useState } from "react";

/* Nabídka motivů = JEDINÝ zdroj pravdy pro přepínač.
 * `swatch` a `preview` musí odpovídat tokenům v index.css — přepínač z nich
 * kreslí barevný náhled, ať poradce vidí, co si vybírá, ne jen název.
 * Přidáváš motiv? Doplň ho sem I do index.css (obě ink-board větve) a přidej
 * ho do testu components/__tests__/prepinaniMotivu.test.js.
 *
 * NENABÍZENÉ MOTIVY (schválně, ať nikdo neklikne na polohotovou věc):
 *  - "elite" (Elite Light) — odebráno 2026-07-04 na přání Pana Čunga.
 *  - "dark" — nikdy nebyl motiv aplikace. `[data-theme="dark"]` v index.css je
 *    jen odstavený shadcn shim, jehož hodnoty se rovnají světlé variantě.
 * Uložená volba mimo tenhle seznam spadne na DEFAULT_THEME (viz readSavedTheme).
 */
export const THEMES = [
  {
    key: "sage",
    name: "Sage & Clay",
    // Popisek je jen ZÁLOHA — překlady do všech pěti jazyků leží ve slovníku
    // pod `motivApp.<klic>` (viz components/ThemeSwitcher.js → popisMotivu).
    tagline: "Teplé, organické, jistota",
    swatch: ["#26221A", "#94583E", "#FAFAF4"],
    preview: { bg: "#EDEDE3", sidebar: "#26221A", surface: "#FAFAF4", accent: "#94583E", ink: "#2B312A", muted: "#585F53", line: "rgba(54,68,59,0.10)", navA: "rgba(255,255,255,0.90)", navB: "rgba(255,255,255,0.45)" },
  },
  {
    key: "indigo",
    name: "Indigo",
    tagline: "Čisté, datasy, fintech",
    swatch: ["#161B2E", "#4F46E5", "#FFFFFF"],
    preview: { bg: "#F4F5F9", sidebar: "#161B2E", surface: "#FFFFFF", accent: "#4F46E5", ink: "#15192B", muted: "#5C6173", line: "rgba(20,25,55,0.08)", navA: "rgba(255,255,255,0.90)", navB: "rgba(255,255,255,0.45)" },
  },
  {
    key: "cobalt",
    name: "Cobalt",
    tagline: "Studený akcent na teplém krému",
    swatch: ["#111C44", "#2457FF", "#F6EFE1"],
    preview: { bg: "#F6EFE1", sidebar: "#111C44", surface: "#FFFDF7", accent: "#2457FF", ink: "#111C44", muted: "#605B53", line: "rgba(17,28,68,0.10)", navA: "rgba(255,255,255,0.94)", navB: "rgba(216,227,255,0.48)" },
  },
  {
    key: "violet",
    name: "Neon Violet",
    tagline: "Výrazné, mladistvé, jiné",
    swatch: ["#2A134D", "#7837DD", "#F1EBFB"],
    preview: { bg: "#F6F2FC", sidebar: "#2A134D", surface: "#FFFFFF", accent: "#7837DD", ink: "#4B228A", muted: "#7045B1", line: "rgba(75,34,138,0.10)", navA: "rgba(255,255,255,0.94)", navB: "rgba(241,235,251,0.46)" },
  },
  {
    key: "lime",
    name: "Electric Lime",
    tagline: "Technické, kontrastní, ostré",
    swatch: ["#0A0A0A", "#C7FF2E", "#F7F7F1"],
    preview: { bg: "#F4F4EF", sidebar: "#0A0A0A", surface: "#FFFFFF", accent: "#0A0A0A", accentFill: "#C7FF2E", ink: "#0A0A0A", muted: "#5B6269", line: "rgba(10,10,10,0.10)", navA: "rgba(255,255,255,0.95)", navB: "rgba(201,208,214,0.46)" },
  },
  {
    key: "midnight",
    name: "Midnight",
    tagline: "Tmavý — na večerní práci",
    swatch: ["#0E1013", "#6186FF", "#1C1F25"],
    preview: { bg: "#14161A", sidebar: "#0E1013", surface: "#1C1F25", accent: "#6186FF", ink: "#F2F4F7", muted: "#979FAA", line: "rgba(255,255,255,0.09)", navA: "rgba(255,255,255,0.94)", navB: "rgba(226,232,240,0.45)" },
  },
];

const STORAGE_KEY = "crm-theme";
const DEFAULT_THEME = "sage";

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  toggleTheme: () => {},
  themes: THEMES,
});

function readSavedTheme() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && THEMES.some((t) => t.key === raw)) return raw;
  } catch { /* SSR / private mode */ }
  return DEFAULT_THEME;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readSavedTheme());

  // Apply theme to <html data-theme="…"> on mount and on every change.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
      // Clear legacy keys from older builds.
      localStorage.removeItem("bp_theme_v3");
      localStorage.removeItem("bp_theme");
    } catch { /* ignore */ }
  }, [theme]);

  const setTheme = (next) => {
    if (!THEMES.some((t) => t.key === next)) return;
    setThemeState(next);
  };

  // Legacy NO-OP for callers expecting toggleTheme().
  const toggleTheme = () => {
    setThemeState((cur) => (cur === "sage" ? "indigo" : "sage"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { theme: DEFAULT_THEME, setTheme: () => {}, toggleTheme: () => {}, themes: THEMES };
  return ctx;
}
